import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PremiumBadge } from '~/components/common/premium-badge';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type {
  AttentionColor,
  BatchAttentionItem,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

import type {
  BatchRetryScope,
  NeedsAttention,
  NeedsAttentionBucket,
  NeedsAttentionCode,
} from '../../types/batch-retry.api.types';
import type { NeedsAttentionClass } from '../../utils/batch-retry-state';

/** Classified Needs Attention (batch pause / retry). Absent = the legacy coverage-fed panel. */
export interface BatchNeedsAttentionRetry {
  needsAttention: NeedsAttention;
  /** RETRY_REMAINING is in the server's `availableActions`. */
  canRetry: boolean;
  /** UNDO_EXCLUSION is in the server's `availableActions`. */
  canUndo: boolean;
  /** Retries are used up with products left — the subtitle says so. */
  stuck: boolean;
  onViewProducts: (cls: NeedsAttentionClass) => void;
  onRetry: (scope: BatchRetryScope) => void;
  onUndo: () => void;
  undoPending: boolean;
}

interface Props {
  /** Classified buckets + per-bucket actions; replaces `total` / `items` when set. */
  retry?: BatchNeedsAttentionRetry;
  total: number;
  items: BatchAttentionItem[];
  /** True for trial batches — shows a premium badge/tooltip next to the
   *  title and blurs the section body since real attention data isn't
   *  available until production. */
  isTrial?: boolean;
}

/**
 * Semantic color → Tailwind chip class + SVG stroke hex.
 * Keeps the legend swatch and donut segment locked to the same token.
 */
const COLOR_STYLES: Record<AttentionColor, { chip: string; hex: string }> = {
  'indigo-dark': { chip: 'bg-indigo-600', hex: '#4f46e5' },
  'indigo-light': { chip: 'bg-indigo-300', hex: '#a5b4fc' },
  sky: { chip: 'bg-sky-300', hex: '#7dd3fc' },
  fuchsia: { chip: 'bg-fuchsia-400', hex: '#e879f9' },
  rose: { chip: 'bg-rose-400', hex: '#fb7185' },
  emerald: { chip: 'bg-emerald-400', hex: '#34d399' },
};

const SEGMENT_GAP_PX = 3;

function DonutChart({ items, total }: { items: BatchAttentionItem[]; total: number }) {
  const { t } = useTranslation('catalog-operations');
  const radius = 52;
  const stroke = 14;
  const circumference = 2 * Math.PI * radius;

  const segments = items.reduce<{ item: BatchAttentionItem; dash: number; offset: number }[]>(
    (acc, item) => {
      const portion = total > 0 ? item.count / total : 0;
      const dash = circumference * portion;
      const last = acc[acc.length - 1];
      const prevOffset = last ? last.offset + last.dash : 0;
      acc.push({ item, dash, offset: prevOffset });
      return acc;
    },
    [],
  );

  // Only carve out a gap when the segment is comfortably larger than the gap itself;
  // otherwise render the full dash so tiny slices remain visible.
  const gapAdjustedDash = (dash: number) =>
    dash > SEGMENT_GAP_PX * 2 ? dash - SEGMENT_GAP_PX : dash;

  return (
    <div data-testid="batch-detail-needs-attention-donut" className="relative h-40 w-40 shrink-0">
      <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        {segments.map(({ item, dash, offset }) => {
          const visibleDash = gapAdjustedDash(dash);
          return (
            <circle
              key={item.label}
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke={COLOR_STYLES[item.color].hex}
              strokeWidth={stroke}
              strokeDasharray={`${String(visibleDash)} ${String(circumference - visibleDash)}`}
              strokeDashoffset={-offset}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <p className="text-muted-foreground text-xs">{t('needsAttention.donutLabel')}</p>
        <p
          data-testid="batch-detail-needs-attention-total"
          className="text-xl font-bold tabular-nums"
        >
          {total.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

const BUCKET_COLOR: Record<NeedsAttentionCode, AttentionColor> = {
  ENRICHMENT_FAILED_RETRYABLE: 'indigo-dark',
  ENRICHMENT_FAILED_PERMANENT: 'rose',
  NOT_ATTEMPTED: 'indigo-light',
  FLAGGED_FOR_REVIEW: 'fuchsia',
};

function bucketScope(code: NeedsAttentionCode): BatchRetryScope | null {
  if (code === 'ENRICHMENT_FAILED_RETRYABLE') return 'FAILED';
  if (code === 'NOT_ATTEMPTED') return 'NOT_ATTEMPTED';
  return null;
}

function bucketClass(code: NeedsAttentionCode): NeedsAttentionClass | null {
  if (code === 'ENRICHMENT_FAILED_RETRYABLE' || code === 'ENRICHMENT_FAILED_PERMANENT') {
    return 'FAILED';
  }
  if (code === 'NOT_ATTEMPTED') return 'NOT_ATTEMPTED';
  return null;
}

function toDonutItems(buckets: NeedsAttentionBucket[]): BatchAttentionItem[] {
  return buckets
    .filter((b) => b.count > 0)
    .map((b) => ({ label: b.label, count: b.count, color: BUCKET_COLOR[b.code] }));
}

function BucketLegend({ retry }: { retry: BatchNeedsAttentionRetry }) {
  const { needsAttention } = retry;
  return (
    <div
      data-testid="batch-detail-needs-attention-list"
      className="grid w-full grid-cols-1 gap-3 rounded-lg bg-white/80 p-3"
    >
      {needsAttention.buckets
        .filter((b) => b.count > 0)
        .map((b) => {
          const scope = bucketScope(b.code);
          const cls = bucketClass(b.code);
          const reasons = b.reasons
            .filter((r) => r.count > 0)
            .map((r) => `${r.label} ${r.count.toLocaleString()}`)
            .join(' · ');
          return (
            <div
              key={b.code}
              data-testid={`batch-detail-needs-attention-bucket-${b.code}`}
              className="flex flex-col gap-1 border-b p-3 last:border-b-0"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground flex items-center gap-2 text-sm">
                  <span
                    className={cn(
                      'h-2.5 w-2.5 rounded-full',
                      COLOR_STYLES[BUCKET_COLOR[b.code]].chip,
                    )}
                  />
                  {b.label}
                </span>
                <span
                  data-testid={`batch-detail-needs-attention-bucket-value-${b.code}`}
                  className="text-sm font-semibold tabular-nums"
                >
                  {b.count.toLocaleString()}
                </span>
              </div>
              {reasons !== '' && <p className="text-muted-foreground text-xs">{reasons}</p>}
              {(cls !== null || (retry.canRetry && b.retryable && scope !== null)) && (
                <div className="flex items-center gap-3 text-xs">
                  {cls && (
                    <button
                      type="button"
                      data-testid={`batch-detail-needs-attention-view-${b.code}`}
                      className="text-primary cursor-pointer font-medium hover:underline"
                      onClick={() => {
                        retry.onViewProducts(cls);
                      }}
                    >
                      View products ›
                    </button>
                  )}
                  {retry.canRetry && b.retryable && scope && (
                    <button
                      type="button"
                      data-testid={`batch-detail-needs-attention-retry-${b.code}`}
                      className="text-primary cursor-pointer font-medium hover:underline"
                      onClick={() => {
                        retry.onRetry(scope);
                      }}
                    >
                      Retry ›
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      {needsAttention.excluded > 0 && (
        <div
          data-testid="batch-detail-needs-attention-excluded"
          className="flex items-center justify-between gap-4 p-3"
        >
          <span className="text-muted-foreground flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-400" />
            Excluded by you
          </span>
          <span className="flex items-center gap-3">
            {retry.canUndo && (
              <button
                type="button"
                data-testid="batch-detail-needs-attention-undo"
                className="text-primary cursor-pointer text-xs font-medium hover:underline disabled:opacity-50"
                disabled={retry.undoPending}
                onClick={retry.onUndo}
              >
                Undo
              </button>
            )}
            <span className="text-sm font-semibold tabular-nums">
              {needsAttention.excluded.toLocaleString()}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

export function BatchNeedsAttention({
  total: legacyTotal,
  items: legacyItems,
  isTrial = false,
  retry,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const total = retry ? retry.needsAttention.total : legacyTotal;
  const items = retry ? toDonutItems(retry.needsAttention.buckets) : legacyItems;
  const excluded = retry?.needsAttention.excluded ?? 0;
  const desktopLastRow = Math.floor((items.length - 1) / 2);

  return (
    <Card data-testid="batch-detail-needs-attention-card">
      <CardHeader className="flex flex-row items-center gap-4 p-6">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <h2 data-testid="batch-detail-needs-attention-title" className="text-xl">
              {t('needsAttention.title')}
            </h2>
            {isTrial && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid="batch-detail-needs-attention-premium-badge"
                    className="inline-flex cursor-help"
                  >
                    <PremiumBadge />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {t('needsAttention.premiumTooltip')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            {retry?.stuck
              ? 'Retries are used up · approve without these or exclude them'
              : t('needsAttention.subtitle')}
          </p>
        </div>
      </CardHeader>

      <CardContent
        className={cn(
          'bg-muted/30 mr-6 mb-6 ml-6 rounded-xl p-6',
          isTrial && 'pointer-events-none blur-[0.99px]',
        )}
      >
        {total === 0 && excluded === 0 ? (
          <div
            data-testid="batch-detail-needs-attention-empty"
            className="flex flex-col items-center justify-center gap-3 py-6 text-center"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
              <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold">No issues found</p>
            <p className="text-muted-foreground max-w-xs text-xs">
              All products in this batch passed enrichment checks.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 md:flex-row">
            <DonutChart items={items} total={total} />
            {retry ? (
              <BucketLegend retry={retry} />
            ) : (
              <div
                data-testid="batch-detail-needs-attention-list"
                className="grid w-full grid-cols-1 gap-x-8 gap-y-3 rounded-lg bg-white/80 p-3 sm:grid-cols-2"
              >
                {items.map((item, index) => {
                  const isMobileLast = index === items.length - 1;
                  const isDesktopLastRow = Math.floor(index / 2) === desktopLastRow;
                  return (
                    <div
                      key={item.label}
                      data-testid={`batch-detail-needs-attention-item-${item.label}`}
                      className={cn(
                        'flex items-center justify-between gap-4 p-4',
                        !isMobileLast && 'border-b',
                        'sm:border-b-0',
                        !isDesktopLastRow && 'sm:border-b',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={cn('h-2.5 w-2.5 rounded-full', COLOR_STYLES[item.color].chip)}
                        />
                        <span className="text-muted-foreground text-sm">{item.label}</span>
                      </div>
                      <span
                        data-testid={`batch-detail-needs-attention-item-value-${item.label}`}
                        className="text-sm font-semibold tabular-nums"
                      >
                        {item.count.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
